import type {
  BoxCapacity,
  BoxCode,
  CompatibilityGroup,
  Variety,
} from "@/lib/catalog/types";
import {
  boxCapacityForLength,
  boxCapacityAny,
  catalogPricePerStem,
  smallestAvailableBox,
  stemsInBox,
} from "@/lib/catalog/pricing";
import { mixCap, subCategoryOf } from "@/lib/catalog/compatibility";
import {
  leadColor,
  mixAllowedForUnit,
  colorForUnit,
} from "@/lib/catalog/color-selection";
import {
  mixGroupOf,
  unitsPerBoxCapFor,
  unitsPerBoxAtSize,
  boxMixGroupLive,
  lineDenom,
  computeBoxFill,
  stemShareKey,
} from "./box-rules";
import type {
  Cart,
  CartAddInput,
  CartAddOutcome,
  CartBox,
  CartBoxCapacity,
  CartLine,
} from "./types";
import { EMPTY_CART } from "./types";
import { getVarietyBySlug as staticVarietyBySlug } from "@/lib/catalog/static-varieties";
import { getCartVarietyLookup, pricedVarietyFor } from "./variety-cache";

// #468 C1: resolve a variety LIVE (client variety cache, primed fresh on cart
// mount) with the static seed only as an offline fallback - never the sole
// source. Used by the +/- and remove re-pack paths so an edited mixGroup /
// unitsPerBox is honored, not the stale static row.
function resolveVarietyLive(slug: string): Variety | undefined {
  return getCartVarietyLookup().get(slug) ?? staticVarietyBySlug(slug) ?? undefined;
}

const BOX_ORDER: BoxCode[] = ["EB", "QB", "HB"];

// #468-ENFORCEMENT A1: float tolerance for fractional box-share sums.
const EPS_PACK = 1e-9;

// Resolves the per-stem price taking colorChoice into account. Delegates
// to catalogPricePerStem (lib/catalog/pricing) so the client and the
// server re-pricing / invoice-build share ONE derivation - a legit
// client price always equals the server re-derive (PASTE #247 CRIT-1).
// `length` here is already resolved by the caller, so the length-fallback
// inside catalogPricePerStem is a no-op.
function resolvePricePerStem(
  variety: Variety,
  length: number | undefined,
  colorChoice?: string,
): number {
  return catalogPricePerStem(variety, length, colorChoice);
}

// True when a box was created by a BOX-mode purchase: a standalone full box
// of one variety. Such boxes are IMMUTABLE - the bunch auto-placement must
// never dissolve, mix into, or re-size them.
export function isBoxModeBox(box: CartBox): boolean {
  return box.contents.length > 0 && box.contents.every((c) => c.unit === "box");
}

// #468-ENFORCEMENT: stems in ONE whole box for a BY-BOX purchase. box_bunch
// reads unitsPerBox (bunches) x stemsPerBunch so an edited count is honored
// (e.g. Be Sweet 70cm QB = 5 -> 125 stems, not the boxSizes 100). box_stem /
// hydrangea (spb null) keeps the boxSizes stem capacity here (A2 wires stems).
function perBoxStemsFor(
  variety: Variety,
  length: number | undefined,
  size: BoxCode,
  cap: BoxCapacity,
): number {
  const spb = variety.stemsPerBunch;
  if (spb !== null && spb > 0) {
    const units = unitsPerBoxAtSize(variety, length, size);
    if (units && units > 0) return units * spb;
  }
  return stemsInBox(cap, size) ?? 0;
}

// BOX MODE: one pure, full box per unit purchased, all of a single variety.
// `line.quantity` is the number of boxes; `line.stems` is the total across
// them. Each emitted box is full at the buyer's chosen size - never stepped
// up, never shared, never partial.
function discreteBoxesFor(
  line: CartLine,
  variety: Variety,
  cap: BoxCapacity,
): CartBox[] {
  const size = line.boxCode ?? smallestAvailableBox(cap);
  if (!size) {
    throw new Error(`No available box size for ${variety.slug}`);
  }
  const stemsPerBox = perBoxStemsFor(variety, line.length, size, cap);
  if (stemsPerBox <= 0) {
    throw new Error(`No stem capacity for ${variety.slug} at ${size}`);
  }

  const boxes: CartBox[] = [];
  for (let i = 0; i < line.quantity; i++) {
    // One CartLine per physical box: quantity 1, a full box of stems. This is
    // what gives each box its own identity downstream (invoice box numbers,
    // the packing list), instead of one fat line spanning several boxes.
    const oneBox: CartLine = {
      ...line,
      quantity: 1,
      stems: stemsPerBox,
      boxCode: size,
    };
    // #468: capacity.stems tracks the unitsPerBox-derived box, so the box-mode
    // "complete" count + wasted-stems display agree with the stored setting.
    boxes.push({
      id: newId(),
      compatibilityGroup: variety.compatibilityGroup,
      size,
      capacity: { stems: stemsPerBox },
      contents: [oneBox],
    });
  }
  return boxes;
}

// Build a CartLine from an add request. Stems are canonical; quantity is
// kept in the user's chosen unit.
function buildLine(input: CartAddInput): CartLine {
  const { variety, length, unit, quantity, colorChoice, boxCode } = input;
  // PASTE #393 (A+B): length-less varieties (specialty/filler, `lengths: []`,
  // post-#362 flat-priced) carry NO length. Ignore whatever length a caller
  // passes so (a) every add produces the SAME null-length line identity and
  // merges into one line, and (b) pricing never mis-resolves - passing a stray
  // length like 50 to a variety priced only at 60 (e.g. Bells) would miss the
  // tier and fall to $0. length-keyed varieties (roses) keep their real length.
  const lengthKeyed = variety.lengths.length > 0;
  const len: number | undefined = lengthKeyed
    ? (length ?? variety.lengths[0])
    : undefined;
  const cap = lengthKeyed
    ? boxCapacityForLength(variety, len as number)
    : boxCapacityAny(variety);
  // DIRECTIVE #023: prefer the caller-provided boxCode when present
  // (resolved by the UI via getDefaultBoxUnit + memory). Fall back to
  // smallestAvailableBox so legacy callers keep working.
  const resolvedCode = cap
    ? boxCode && cap[boxCode] !== null
      ? boxCode
      : smallestAvailableBox(cap)
    : null;
  // #532 cart-save: never price a line from a price-stripped record when the
  // real (client-priced) record is known - a $0 line is refused on save.
  const stem = resolvePricePerStem(pricedVarietyFor(variety), len, colorChoice);

  let stems: number;
  if (unit === "bunch") {
    if (variety.stemsPerBunch === null) {
      throw new Error(`Cannot add ${variety.slug} as bunch - no stemsPerBunch`);
    }
    stems = quantity * variety.stemsPerBunch;
  } else if (unit === "stem") {
    // #63 box_stem: loose stems of a box_stem variety (hydrangea). `quantity` IS
    // the stem count and stems === quantity (stemsPerBunch stays null). The
    // packer (packStemBucket) sizes EB/QB boxes from the grade's unitsPerBox.
    stems = quantity;
  } else {
    // unit === "box": quantity is number of boxes of the resolved size.
    if (!cap || !resolvedCode) {
      throw new Error(`No available box size for ${variety.slug}`);
    }
    // #468-ENFORCEMENT: a whole box holds unitsPerBox units (bunches x spb for
    // box_bunch), not the raw boxSizes stem cap.
    const boxStems = perBoxStemsFor(variety, len, resolvedCode, cap);
    if (boxStems <= 0) {
      throw new Error(`No stem capacity for ${variety.slug} at ${resolvedCode}`);
    }
    stems = quantity * boxStems;
  }

  return {
    slug: variety.slug,
    variety: variety.name,
    category: variety.category,
    length: variety.purchaseUnit === "box" ? undefined : len,
    unit,
    quantity,
    stems,
    pricePerStem: stem,
    stemsPerBunch: variety.stemsPerBunch,
    colorChoice,
    // #468 STABLE ORDER: honor an add time threaded through the replay; a genuine
    // new add has none -> stamp now.
    addedAt: input.addedAt ?? new Date().toISOString(),
    // DIRECTIVE #024: preserve the resolved box size so the V3 packer's
    // hydrangea/box-unit path honors the buyer's pick instead of
    // defaulting to smallestAvailableBox. #63: a stem line carries no boxCode -
    // the stem packer sizes its boxes from unitsPerBox + the group size choice.
    boxCode: unit === "stem" ? undefined : (resolvedCode ?? undefined),
    // #468-ENFORCEMENT A1: stamp the LIVE packing settings so fill + bucketing
    // read the stored data (fractional shares + merged mix groups), not the
    // derived matrix or the coarse compatibility group.
    unitsPerBox: unitsPerBoxCapFor(variety, len) ?? undefined,
    mixGroup: mixGroupOf(variety),
    // #468 SIZE DRIVES PACKING: carry the group's chosen size (new adds inherit
    // it via addToBox / the bucket's stamped items; the packer fills to it).
    groupBoxSize: input.groupBoxSize,
  };
}

// Effective capacity for a box - applies a mix rule when the box mixes
// categories within group A. Other groups always use raw capacity.
export function effectiveCapacity(
  box: CartBox,
  newVariety: Variety | null,
): CartBoxCapacity {
  if (box.compatibilityGroup !== "A") return box.capacity;
  // Only group A has a mixRule. Look at all varieties (existing + incoming)
  // and detect whether the box is mixed.
  const categories = new Set(box.contents.map((c) => c.category));
  if (newVariety) categories.add(newVariety.category);
  if (categories.size <= 1) return box.capacity;
  if (!newVariety || !newVariety.mixRule) return box.capacity;
  // The mix rule applies. We need to find its cap for the current size.
  if (box.size === "QB" || box.size === "HB") {
    const cap = mixCap(newVariety.mixRule, box.size);
    if (!cap) return box.capacity;
    return { stems: cap.stems, bunches: cap.bunches };
  }
  return box.capacity;
}

// Returns true when the line can be appended to box without exceeding the
// stem/bunch cap. #468 C1: renamed from fitsInBox -> fitsWithinCapacity so the
// ONE share/suggest decision named fitsInBox lives only in box-rules.ts. This is
// the dormant #353 group-A mix-cap capacity guard (no live packer caller today;
// preserved, not deleted, per the #353 "keep dormant" rule).
export function fitsWithinCapacity(
  box: CartBox,
  incomingVariety: Variety,
  additionalStems: number,
  additionalBunches: number,
): boolean {
  const cap = effectiveCapacity(box, incomingVariety);
  const currentStems = box.contents.reduce((s, c) => s + c.stems, 0);
  if (currentStems + additionalStems > cap.stems) return false;
  if (cap.bunches !== undefined) {
    const currentBunches = box.contents.reduce(
      (b, c) => b + (c.unit === "bunch" ? c.quantity : 0),
      0,
    );
    if (currentBunches + additionalBunches > cap.bunches) return false;
  }
  return true;
}

function rawCapacityFor(
  capacity: BoxCapacity,
  code: BoxCode,
): CartBoxCapacity | null {
  const stems = capacity[code];
  if (stems === null) return null;
  return { stems };
}

// After a write, if the box now contains varieties from multiple categories
// within group A, stamp the mix-rule cap onto the box so summarize +
// minimum-order checks see the effective cap.
function applyMixCapIfNeeded(
  box: CartBox,
  incoming: Variety,
  newContents: CartLine[],
): CartBoxCapacity {
  if (box.compatibilityGroup !== "A") return box.capacity;
  const categories = new Set(newContents.map((c) => c.category));
  if (categories.size <= 1) return box.capacity;
  if (!incoming.mixRule) return box.capacity;
  if (box.size === "QB" || box.size === "HB") {
    const cap = mixCap(incoming.mixRule, box.size);
    if (cap) return { stems: cap.stems, bunches: cap.bunches };
  }
  return box.capacity;
}

// Picks the smallest available box code that has enough stem capacity to
// hold the incoming line. Returns null if no available size fits.
export function chooseInitialBoxSize(
  capacity: BoxCapacity,
  stemsNeeded: number,
): BoxCode | null {
  for (const code of BOX_ORDER) {
    const stems = capacity[code];
    if (stems === null) continue;
    if (stems >= stemsNeeded) return code;
  }
  return null;
}

// Tries to upgrade a box to the next-larger non-null size. Returns the
// upgraded size or null if no larger size exists.
export function nextLargerSize(
  capacity: BoxCapacity,
  current: BoxCode,
): BoxCode | null {
  const idx = BOX_ORDER.indexOf(current);
  for (let i = idx + 1; i < BOX_ORDER.length; i++) {
    if (capacity[BOX_ORDER[i]] !== null) return BOX_ORDER[i];
  }
  return null;
}


// A box counts as "full" when it has reached its effective capacity. PASTE
// #379: the fill math now flows through ONE source of truth (computeBoxFill),
// so the gate and every displayed number agree - including for varieties
// whose stems-per-bunch does not divide the box's stem capacity evenly (a
// box packed to the packer's max now reads full instead of stalling one
// short). computeBoxFill.full is a superset of the old stems/bunch gate, so
// meetsMinimum never flips true -> false for an already-valid cart (see the
// header of lib/cart/box-fill.ts).
export function isBoxFull(box: CartBox): boolean {
  return computeBoxFill(box).full;
}

// Does this group have a box that is full at its (effective) capacity?
// Matches the "fill the box" minimum-order rule (Option A: group independence).
export function groupMeetsMinimum(
  cart: Cart,
  group: CompatibilityGroup,
): boolean {
  for (const box of cart.boxes) {
    if (box.compatibilityGroup !== group) continue;
    if (isBoxFull(box)) return true;
  }
  return false;
}

// Recompute derived fields (subtotal + meetsMinimum) from the boxes.
// smallestPerGroup is no longer used for the minimum check now that boxes
// carry their effective capacity, but is kept in the signature so future
// per-group minimums can be plumbed without an API break.
export function recompute(cart: Cart): Cart {
  let subtotal = 0;
  for (const box of cart.boxes) {
    for (const line of box.contents) {
      subtotal += line.stems * line.pricePerStem;
    }
  }
  // #468 OPTION-B: NO PARTIAL BOXES. Checkout unlocks only when EVERY box is a
  // valid completed size (any offered EB/QB/HB, via computeBoxFill.full) - not
  // the old "some() box is full". An empty cart is not orderable.
  const meets = cart.boxes.length > 0 && cart.boxes.every(isBoxFull);
  return {
    boxes: cart.boxes,
    subtotal: Math.round(subtotal * 100) / 100,
    meetsMinimum: meets,
  };
}

// Step 7.1 commit 4 / AC247: per-line decrement / remove helpers.
// Identifies the line by box id + slug + length + color (the same
// composite key the drawer uses for its React key). Returns a new
// Cart with the line either updated, removed, or - if removing the
// last line in a box - the entire box pulled out of the cart.

export type CartLineKey = {
  boxId: string;
  slug: string;
  length?: number;
  colorChoice?: string;
};

function matchesLine(line: CartLine, key: Omit<CartLineKey, "boxId">): boolean {
  return (
    line.slug === key.slug &&
    line.length === key.length &&
    (line.colorChoice ?? null) === (key.colorChoice ?? null)
  );
}

export function changeLineQuantity(
  cart: Cart,
  key: CartLineKey,
  delta: number,
): Cart {
  if (delta === 0) return cart;

  // PASTE #382: decide up-front (from the ORIGINAL box) whether this is a
  // re-packable single-category bunch box. If so, the in-place bump below may
  // transiently overflow the box, but the packer re-pack afterwards
  // redistributes the bunches into correctly-sized boxes (grow / spill) just
  // like ADD. Box-mode + group-A mix boxes fall through to the in-place result
  // exactly as before (never dissolved).
  const targetBox = cart.boxes.find((b) => b.id === key.boxId);
  const targetLine = targetBox?.contents.find((l) => matchesLine(l, key));
  const variety = targetLine ? resolveVarietyLive(targetLine.slug) : undefined;
  const repackable =
    !!targetBox &&
    !!targetLine &&
    !!variety &&
    isRepackableBunchBox(targetBox, targetLine);

  const boxes: CartBox[] = [];
  for (const box of cart.boxes) {
    if (box.id !== key.boxId) {
      boxes.push(box);
      continue;
    }
    const nextContents: CartLine[] = [];
    for (const line of box.contents) {
      if (!matchesLine(line, key)) {
        nextContents.push(line);
        continue;
      }
      const nextQty = line.quantity + delta;
      if (nextQty <= 0) continue; // drop the line entirely
      const perUnitStems =
        line.quantity > 0 ? line.stems / line.quantity : line.stems;
      nextContents.push({
        ...line,
        quantity: nextQty,
        stems: Math.round(perUnitStems * nextQty),
      });
    }
    if (nextContents.length > 0) {
      boxes.push({ ...box, contents: nextContents });
    }
    // else: box becomes empty, omit it
  }
  const mutated = { ...cart, boxes };
  if (repackable) {
    return repackBucket(mutated, variety!, targetLine!.length);
  }
  return recompute(mutated);
}

// DIRECTIVE #020 (Fix 1, Q4): repack all matching-line boxes (same
// slug+length+colorChoice) from scratch using the new greedy + step-up
// rule. Called by the cart-banner "Add X stems to complete..." +
// "Consolidate" buttons so the end state matches the button's promise
// rather than incrementally fitting into the existing partial box.
// `stemsDelta` is added to the existing same-line total (negative for
// consolidate).
export function repackSameLine(
  cart: Cart,
  variety: Variety,
  length: number | undefined,
  colorChoice: string | undefined,
  stemsDelta: number,
): Cart {
  const cap = boxCapacityForLength(variety, length ?? variety.lengths[0] ?? 0);
  if (!cap) return cart;
  const sameKey = (l: CartLine) =>
    l.slug === variety.slug &&
    l.length === length &&
    (l.colorChoice ?? null) === (colorChoice ?? null);

  // Sum existing matching-line stems + carry forward the canonical
  // pricePerStem + stemsPerBunch + unit from the first matching line so
  // the rebuilt lines look identical to the originals.
  let totalStems = 0;
  let template: CartLine | null = null;
  const otherBoxes: CartBox[] = [];
  for (const box of cart.boxes) {
    const sameLines = box.contents.filter(sameKey);
    const otherLines = box.contents.filter((l) => !sameKey(l));
    for (const l of sameLines) {
      totalStems += l.stems;
      if (!template) template = l;
    }
    if (otherLines.length > 0) {
      otherBoxes.push({ ...box, contents: otherLines });
    }
  }

  totalStems += stemsDelta;
  if (totalStems <= 0 || !template) {
    return recompute({ ...cart, boxes: otherBoxes });
  }

  const spb = template.stemsPerBunch ?? 0;
  const rebuiltLine: CartLine = {
    ...template,
    stems: totalStems,
    quantity:
      template.unit === "bunch" && spb > 0
        ? Math.round(totalStems / spb)
        : Math.max(1, Math.round(totalStems / Math.max(1, template.stems))),
  };
  const newBoxes = packBunchesIntoBoxes(rebuiltLine, variety, cap, totalStems);
  return recompute({ ...cart, boxes: [...otherBoxes, ...newBoxes] });
}

// DIRECTIVE #021: drop just the leftover-bunches partial box. Full
// boxes elsewhere in the cart stay untouched. Called by the banner's
// tertiary "Remove this partial box" button. No confirm dialog -
// the verbose button label IS the confirmation per Q3.
export function removePartialBox(cart: Cart, boxId: string): Cart {
  return recompute({
    ...cart,
    boxes: cart.boxes.filter((b) => b.id !== boxId),
  });
}

export function removeLine(cart: Cart, key: CartLineKey): Cart {
  // PASTE #382: mirror changeLineQuantity - after a removal, re-pack the
  // affected single-category bunch bucket so the remaining boxes consolidate,
  // consistent with the re-pack path. Box-mode + mix boxes keep the plain
  // filter behavior (never dissolved).
  const targetBox = cart.boxes.find((b) => b.id === key.boxId);
  const targetLine = targetBox?.contents.find((l) => matchesLine(l, key));
  const variety = targetLine ? resolveVarietyLive(targetLine.slug) : undefined;
  const repackable =
    !!targetBox &&
    !!targetLine &&
    !!variety &&
    isRepackableBunchBox(targetBox, targetLine);
  const removedLen = targetLine?.length;

  const boxes: CartBox[] = [];
  for (const box of cart.boxes) {
    if (box.id !== key.boxId) {
      boxes.push(box);
      continue;
    }
    const nextContents = box.contents.filter(
      (l) => !matchesLine(l, key),
    );
    if (nextContents.length > 0) {
      boxes.push({ ...box, contents: nextContents });
    }
  }
  const mutated = { ...cart, boxes };
  if (repackable) {
    return repackBucket(mutated, variety!, removedLen);
  }
  return recompute(mutated);
}

// #74 part-2: change an already-added cart line's COLOR in place, WITHOUT the
// buyer having to remove + re-add. A color change is a new line identity, so
// this composes the EXISTING canonical mutations: removeLine(oldKey) then
// addToBox(new color). Both route through the canonical packer, so the result
// dedups (if the target color already exists in the box), re-packs, and keeps
// client == server == invoice parity. NO new packing logic here.
//
// Two shapes:
//   - colorOptions variety (roses / specialty): color is a VARIANT (colorChoice)
//     on the SAME slug. `target` is the colorChoice. "mix" is honored only when
//     the line's unit allows it (box only), else rejected (Mix stays box-only).
//   - hydrangea: each color is a SEPARATE SKU (grade unchanged). `target` is the
//     sibling color's SLUG; color rides the slug, so no colorChoice is passed.
export function changeLineColor(
  cart: Cart,
  key: CartLineKey,
  target: string,
): Cart {
  const box = cart.boxes.find((b) => b.id === key.boxId);
  const line = box?.contents.find((l) => matchesLine(l, key));
  if (!box || !line) return cart;
  const variety = resolveVarietyLive(line.slug);
  if (!variety) return cart;

  const hasColorOptions = (variety.colorOptions?.length ?? 0) > 0;

  if (hasColorOptions) {
    const current = line.colorChoice ?? leadColor(variety);
    if (target === current) return cart;
    // Mix is a by-box option only; never allow it onto a bunch/stem line.
    if (target === "mix" && !mixAllowedForUnit(variety, line.unit)) return cart;
    const removed = removeLine(cart, key);
    const out = addToBox(removed, {
      variety,
      length: line.length,
      unit: line.unit,
      quantity: line.quantity,
      colorChoice: colorForUnit(variety, target, line.unit) || undefined,
    });
    return out.kind === "added" ? out.cart : removed;
  }

  // hydrangea (color = separate SKU): swap to the target color's SKU, same
  // grade/unit/quantity. Color rides the slug (no colorChoice).
  if (target === line.slug) return cart;
  const targetVariety = resolveVarietyLive(target);
  if (!targetVariety) return cart;
  const removed = removeLine(cart, key);
  const out = addToBox(removed, {
    variety: targetVariety,
    // hydrangea has no length concept; carry the box size for a whole-box line.
    length: undefined,
    unit: line.unit,
    quantity: line.quantity,
    boxCode: line.unit === "box" ? line.boxCode : undefined,
  });
  return out.kind === "added" ? out.cart : removed;
}

function newId(): string {
  // crypto.randomUUID is available in modern Node + browsers.
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `box-${Math.random().toString(36).slice(2)}`;
}

// Public API: places the new line and returns { kind: "added", cart, boxId }.
// (#430: the old "mix-conflict" outcome + MixConflict modal were removed with
// the Rose+Garden cross-mix; adds always succeed into the correct bucket.)
//
// Options:
//   forceAccept    - skip the (now-removed) mix-conflict check; used by the
//                    canonical replay (materializeCart) + reorder/upsell
//   forceNewBox    - always open a new pure box (no fit-existing)
//   targetBoxId    - DIRECTIVE #021 Q2: explicit mix-in via the inline
//                    variety strip. Adds the new variety's bunches into
//                    the named partial box (respecting mix-rule cap). If
//                    the mix-cap would be exceeded, falls through to a
//                    pure new box (AC202).
export function addToBox(
  cart: Cart,
  input: CartAddInput,
  options: {
    forceAccept?: boolean;
    forceNewBox?: boolean;
    targetBoxId?: string;
  } = {},
): CartAddOutcome {
  const { variety } = input;
  const len = input.length ?? variety.lengths[0];
  const cap = boxCapacityForLength(variety, len);
  if (!cap) {
    throw new Error(`${variety.slug} has no boxSizes data`);
  }

  const line = buildLine(input);
  const stemsToAdd = line.stems;
  const bunchesToAdd = line.unit === "bunch" ? line.quantity : 0;

  // ==========================================================================
  // #63 box_stem: loose STEMS. A stem add always routes through the mixGroup
  // bucket packer (dissolve -> packBucket -> packStemBucket) for BOTH the
  // interactive add AND the forceAccept replay (materializeCart), so the client
  // cart, the server re-pack, and the invoice pack identically. packStemBucket
  // partitions by GRADE (one grade per box) and drains stems into EB/QB boxes.
  // Whole-box hydrangea boxes (unit "box") are box-mode/immutable and are never
  // dissolved, so they coexist untouched.
  // ==========================================================================
  if (line.unit === "stem") {
    const { otherBoxes, bucketItems } = dissolveBucket(cart, variety);
    mergeBucketItem(bucketItems, lineToItem(line));
    const newBoxes = packBucket(bucketItems, variety, cap);
    if (newBoxes.length === 0) {
      throw new Error(`Cannot place ${variety.slug}: stem pack returned 0 boxes`);
    }
    return {
      kind: "added",
      cart: recompute({ ...cart, boxes: [...otherBoxes, ...newBoxes] }),
      boxId: newBoxes[0].id,
    };
  }

  // ==========================================================================
  // BOX MODE (locked rule, PROJECT_MEMORY Step 6.7.1): "Buy by box" means each
  // box is a STANDALONE FULL box of JUST this variety. 5 QB of 5 varieties =
  // 5 separate QB boxes. No cross-variety consolidation, no step-up to a
  // larger size, no split lines, no partial boxes.
  //
  // Before this, box-mode adds fell through into the V3/V4 BUNCH bucket
  // repack below, which converted them to bunch-equivalents and re-packed
  // them: 3 box-mode QB roses came out as one HB(300) holding all three
  // varieties plus a partial QB(75/125). The old "one pure box per unit"
  // branch inside packBucket only triggered when stemsPerBunch was null -
  // i.e. hydrangeas only - so every bunch-priced variety (roses, spray,
  // specialty, fillers) bought BY BOX was mis-packed.
  //
  // Emit the boxes directly and return. This runs BEFORE the mix-conflict
  // check (a pure single-variety box can never trigger a mix transition) and
  // before every repack path, including forceAccept - so the server-side
  // replay in materializeCart cannot mix box-mode boxes either.
  // ==========================================================================
  if (line.unit === "box") {
    const boxes = discreteBoxesFor(line, variety, cap);
    return {
      kind: "added",
      cart: recompute({ ...cart, boxes: [...cart.boxes, ...boxes] }),
      boxId: boxes[0].id,
    };
  }

  // Find candidate open boxes for this variety's BUCKET. #429: scope by
  // sub-category (A-rose vs A-gardenrose, D-lily vs D), not the coarse group,
  // so the canonical re-pack (forceAccept path, materializeCart) can never fit
  // a standard rose into a garden-rose box, or a lily into another specialty
  // box. Sub-category equality implies same group, so this is strictly tighter.
  // #429/#430: candidate open boxes for this variety's BUCKET (sub-category:
  // A-rose vs A-gardenrose, D-lily vs D). The old group-A "mix-conflict"
  // detection was removed with the Rose+Garden cross-mix (#353) - buckets can
  // never mix, so there is nothing to detect.
  // #468-ENFORCEMENT A1: candidate open boxes are scoped by MIX GROUP now
  // (Alexis's merged groups), not sub-category. Box-mode boxes are standalone
  // + immutable, never a candidate.
  const incomingGroup = mixGroupOf(variety);
  const groupBoxes = cart.boxes.filter(
    (b) => !isBoxModeBox(b) && boxMixGroup(b) === incomingGroup,
  );

  // DIRECTIVE #021 Q2: targetBoxId path (explicit mix-in from the strip).
  // Try to mix the new bunches into the named partial box, respecting
  // mix-rule cap. If the mix-cap would be exceeded, AC202: fall through
  // to a pure new box.
  if (options.targetBoxId) {
    const target = cart.boxes.find((b) => b.id === options.targetBoxId);
    // Never mix bunches into a standalone BOX-mode box.
    if (target && !isBoxModeBox(target)) {
      const newContents = [...target.contents, line];
      const mixCap = applyMixCapIfNeeded(target, variety, newContents);
      const projectedStems =
        target.contents.reduce((s, c) => s + c.stems, 0) + stemsToAdd;
      if (projectedStems <= mixCap.stems) {
        const updatedBox: CartBox = {
          ...target,
          contents: newContents,
          capacity: mixCap,
        };
        const next = replaceBox(cart, updatedBox);
        return { kind: "added", cart: recompute(next), boxId: updatedBox.id };
      }
      // Cap exceeded -> AC202 fallback: open a pure new box for the
      // incoming variety alongside the existing partial.
    }
  }

  // DIRECTIVE #022/#026: per-bucket repack. V3 bucket key was
  // (subCategory, length); DIRECTIVE #026 Change 1 consolidates Group A
  // (both A-rose and A-gardenrose) under a subCategory-only key so
  // 50/60/70 cm bunches share one A-rose bucket and the V4 dynamic
  // QB cap can interleave them. B/C/D/E keep the (subCategory, length)
  // key since their lengths don't cross-mix.
  if (!options.forceNewBox && !options.forceAccept) {
    const { otherBoxes, bucketItems } = dissolveBucket(cart, variety);
    mergeBucketItem(bucketItems, lineToItem(line));

    if (bucketItems.length === 0) {
      otherBoxes.push(...cart.boxes); // defensive: no bucket items, shouldn't happen
    }

    const newBoxes = packBucket(bucketItems, variety, cap);
    if (newBoxes.length === 0) {
      throw new Error(
        `Cannot place ${variety.slug}: bucket pack returned 0 boxes`,
      );
    }
    return {
      kind: "added",
      cart: recompute({ ...cart, boxes: [...otherBoxes, ...newBoxes] }),
      boxId: newBoxes[0].id,
    };
  }

  // forceAccept (post-mix-rebalance from the legacy MixConflict modal):
  // fit into the first group box that has room under mix-rule cap. This
  // is the explicit "mix and rebalance" path - the buyer accepted that
  // their new variety will share an existing box with a different
  // category, and we honor that intent.
  if (options.forceAccept) {
    for (const box of groupBoxes) {
      if (box.contents.length === 0) continue;
      // A BOX-mode box is full and single-variety by definition - never
      // rebalance a bunch into it.
      if (isBoxModeBox(box)) continue;
      // #468-ENFORCEMENT A1: fit is FRACTIONAL now - the whole incoming line
      // must keep the box's box-share at or below 1.0 for its OWN length's
      // denominator. (The stem-based fitsWithinCapacity stays for the dormant #353 cap.)
      const fill = computeBoxFill(box);
      const denom = unitsPerBoxAtSize(variety, line.length, box.size);
      const fits =
        !fill.full &&
        !!denom &&
        denom > 0 &&
        fill.totalShare + bunchesToAdd / denom <= 1 + EPS_PACK;
      if (fits) {
        const newContents = [...box.contents, line];
        const updatedBox: CartBox = {
          ...box,
          contents: newContents,
          capacity: applyMixCapIfNeeded(box, variety, newContents),
        };
        const next = replaceBox(cart, updatedBox);
        return { kind: "added", cart: recompute(next), boxId: box.id };
      }
    }
  }

  // forceNewBox or forceAccept-with-no-fit: open a pure new box for the
  // incoming line via V2 packer.
  const newBoxes = packBunchesIntoBoxes(line, variety, cap, stemsToAdd);
  if (newBoxes.length === 0) {
    throw new Error(
      `Cannot place ${variety.slug}: needs ${stemsToAdd} stems, no box size fits`,
    );
  }
  return {
    kind: "added",
    cart: recompute({ ...cart, boxes: [...cart.boxes, ...newBoxes] }),
    boxId: newBoxes[0].id,
  };
}

// DIRECTIVE #022 V3: cross-variety bucket item. The packer drains items
// FIFO by addedAt across varieties to produce mixed-variety boxes.
type BucketItem = {
  slug: string;
  variety: string;
  category: CartLine["category"];
  length: number | undefined;
  colorChoice: string | undefined;
  bunches: number;
  stemsPerBunch: number;
  pricePerStem: number;
  // #63: "stem" = a loose-stem box_stem line; `bunches` then holds the STEM
  // count (each stem is one atomic sale unit). packStemBucket drains it.
  unit: "bunch" | "box" | "stem";
  addedAt: string;
  // DIRECTIVE #024: explicit box code the buyer picked. The V3 packer's
  // hydrangea/box-unit path (stemsPerBunch === 0) reads this to size
  // the resulting box instead of always falling back to smallest.
  boxCode?: BoxCode;
  // #468-ENFORCEMENT A1: carried from the CartLine so the fractional packer +
  // computeBoxFill see the live per-box unit counts + merged mix group.
  unitsPerBox?: BoxCapacity;
  mixGroup?: number | null;
  // #468 SIZE DRIVES PACKING: the group's chosen box size, carried from the
  // CartLine so the fractional packer fills the group's boxes to it.
  groupBoxSize?: BoxCode;
};

function lineToItem(line: CartLine): BucketItem {
  const spb = line.stemsPerBunch ?? 0;
  const bunches =
    line.unit === "bunch"
      ? line.quantity
      : spb > 0
        ? Math.round(line.stems / spb)
        : line.quantity;
  return {
    slug: line.slug,
    variety: line.variety,
    category: line.category,
    length: line.length,
    colorChoice: line.colorChoice,
    bunches,
    stemsPerBunch: spb,
    pricePerStem: line.pricePerStem,
    unit: line.unit,
    addedAt: line.addedAt ?? new Date().toISOString(),
    boxCode: line.boxCode,
    unitsPerBox: line.unitsPerBox,
    mixGroup: line.mixGroup,
    groupBoxSize: line.groupBoxSize,
  };
}

// Merge same-slug+length+color items, keeping the earliest addedAt.
function mergeBucketItem(items: BucketItem[], incoming: BucketItem): void {
  for (const existing of items) {
    if (
      existing.slug === incoming.slug &&
      (existing.length ?? null) === (incoming.length ?? null) &&
      (existing.colorChoice ?? null) === (incoming.colorChoice ?? null)
    ) {
      existing.bunches += incoming.bunches;
      if (incoming.addedAt < existing.addedAt) existing.addedAt = incoming.addedAt;
      // #468 SIZE DRIVES PACKING: keep whichever copy carries the group's choice.
      existing.groupBoxSize = existing.groupBoxSize ?? incoming.groupBoxSize;
      return;
    }
  }
  items.push({ ...incoming });
}

// #468-ENFORCEMENT A1: the box's mix group (from its first line's stamped
// mixGroup). This is the who-can-share-a-box key now - Alexis's merged groups,
// not the coarse compatibility group / sub-category. All lines in a box share
// one mix group (the bucket invariant). null = a box-mode / box_only box.
export function boxMixGroup(box: CartBox): number | null {
  // #468 C1: the ONE resolver - LIVE by anchor slug (box-rules), never the
  // stale stamp / static seed as the primary source.
  return boxMixGroupLive(box);
}

// PASTE #382: dissolve the bucket that `variety` (at `targetLen`) packs into,
// exactly as addToBox does on ADD: pull every same-subcategory / same-group
// bucket box (A-rose/A-gardenrose merge across lengths, others match length)
// into a flat BucketItem list, leaving BOX-mode boxes untouched. Returns the
// leftover boxes plus the dissolved items so the caller can re-pack. Extracted
// verbatim from addToBox so ADD behavior is unchanged - both paths reuse it.
function dissolveBucket(
  cart: Cart,
  variety: Variety,
): { otherBoxes: CartBox[]; bucketItems: BucketItem[] } {
  // #468-ENFORCEMENT A1: the bucket is the MIX GROUP - ALL lengths (roses) and
  // grades/colors (hydrangea) merge freely within it. No sub-category or length
  // matching. BOX-mode boxes are standalone + immutable and never dissolved.
  const targetGroup = mixGroupOf(variety);
  const otherBoxes: CartBox[] = [];
  const bucketItems: BucketItem[] = [];
  for (const box of cart.boxes) {
    const inBucket =
      targetGroup !== null &&
      boxMixGroup(box) === targetGroup &&
      !isBoxModeBox(box);
    if (!inBucket) {
      otherBoxes.push(box);
      continue;
    }
    for (const existing of box.contents) {
      mergeBucketItem(bucketItems, lineToItem(existing));
    }
  }
  return { otherBoxes, bucketItems };
}

// PASTE #382: re-pack the bucket `variety` (at `targetLen`) belongs to, using
// the SAME dissolve -> packBucket path addToBox uses on ADD. Called after a
// quantity mutation (+/-) or a removal so the affected bucket is re-optimized
// (an increment that overflowed grows to the next box / spills into a new box
// via the existing largest-first logic; a decrement / removal consolidates).
// No incoming line is merged - the mutated quantities are already in the boxes.
function repackBucket(
  cart: Cart,
  variety: Variety,
  targetLen: number | undefined,
): Cart {
  const len =
    targetLen ?? variety.lengths[0] ?? variety.pricePerStem[0]?.length;
  const cap = len !== undefined ? boxCapacityForLength(variety, len) : null;
  if (!cap) return recompute(cart);
  const { otherBoxes, bucketItems } = dissolveBucket(cart, variety);
  if (bucketItems.length === 0) {
    return recompute({ ...cart, boxes: otherBoxes });
  }
  const newBoxes = packBucket(bucketItems, variety, cap);
  return recompute({ ...cart, boxes: [...otherBoxes, ...newBoxes] });
}

// PASTE #382: a bunch box safe to route through the packer re-pack. Excludes
// BOX-mode boxes (immutable, never dissolved), group-A mix boxes (carry an
// effective bunch cap - packBucket doesn't apply mix-rule caps, so they stay
// on the in-place path), and cross-category boxes. Single-category bunch boxes
// (the overflow case: roses / specialty / filler) are re-packable.
function isRepackableBunchBox(box: CartBox, line: CartLine): boolean {
  if (line.unit !== "bunch") return false;
  if (isBoxModeBox(box)) return false;
  if (box.capacity.bunches !== undefined) return false; // group-A mix box
  const categories = new Set(box.contents.map((c) => c.category));
  return categories.size <= 1;
}

// #468-ENFORCEMENT A1: fractional bunch packer. Drains a mixGroup bucket's
// per-bunch units FIFO into boxes - each bunch is 1/unitsPerBox[size][its
// length] of a box, and a box closes when the next bunch would push the running
// share over 1.0. Largest available size first while a full large box's worth
// remains, then the smallest available size for the tail - so box COUNTS match
// the pre-#468 grow-to-largest packing for the default seed (where unitsPerBox
// == floor(cap/spb)), while honoring edited counts + rose length mixing.
// Replaces packAroseV4's length cap + the growBoxPlan spb path.
type FlatUnit = { item: BucketItem; length: number | undefined };

function shareAtSize(u: FlatUnit, size: BoxCode): number {
  // #468 C1: the packer's box-share denominator comes from the ONE box-rules
  // resolver (LIVE by slug, stamp last resort) - same source as computeBoxFill.
  const d = lineDenom(u.item, size);
  return d && d > 0 ? 1 / d : 0;
}

// Drain FIFO from flat[startIdx..] into ONE box of `size`, adding bunches until
// the next would push the running fractional share over 1.0.
function fillOneBox(
  flat: FlatUnit[],
  startIdx: number,
  size: BoxCode,
): { units: FlatUnit[]; nextIdx: number; share: number } {
  const units: FlatUnit[] = [];
  let share = 0;
  let i = startIdx;
  while (i < flat.length) {
    const inc = shareAtSize(flat[i], size);
    if (inc <= 0) break; // this variety does not offer `size`
    if (units.length > 0 && share + inc > 1 + EPS_PACK) break;
    share += inc;
    units.push(flat[i]);
    i += 1;
  }
  if (units.length === 0 && i < flat.length) {
    units.push(flat[i]);
    i += 1;
  }
  return { units, nextIdx: i, share };
}

function packBunchBucket(
  items: BucketItem[],
  refVariety: Variety,
  cap: BoxCapacity,
): CartBox[] {
  const queue = [...items]
    .filter((i) => i.bunches > 0)
    .sort((a, b) => {
      const t = a.addedAt.localeCompare(b.addedAt);
      return t !== 0 ? t : a.slug.localeCompare(b.slug);
    });
  const flat: FlatUnit[] = [];
  for (const it of queue) {
    for (let i = 0; i < it.bunches; i++) flat.push({ item: it, length: it.length });
  }
  if (flat.length === 0) return [];

  // #468 SIZE DRIVES PACKING: the group's client-chosen box size (Small/Standard/
  // Large = EB/QB/HB), if any line in the bucket carries one. The packer fills the
  // group's boxes to THIS size (consolidating into fewer larger boxes, or splitting
  // into more smaller ones) instead of the QB/HB-threshold default. Stamp every
  // item so the re-materialized lines all carry it (persists + invoice parity).
  const groupTarget = queue.find((i) => i.groupBoxSize)?.groupBoxSize;
  if (groupTarget) for (const it of queue) it.groupBoxSize = groupTarget;

  const isARose = subCategoryOf(refVariety) === "A-rose";

  // #468 ROSE LENGTH-MIX (locked Sep 10): roses (category=rose) mix any 50/60/70
  // in ONE box - do NOT partition them by denom. The rose box's per-size capacity
  // is the MAX per-length denom of its contents (all-70 QB = 4, a QB with any
  // 50/60 = 5, HB = 12), computed live via lineDenom (the single box-rules source,
  // same as computeBoxFill). Every OTHER bucket keeps "one box = one denom" below,
  // so non-rose orders stay byte-identical.
  if (isARose) return packAroseMix(flat, refVariety, groupTarget);

  // #468 OPTION-B: partition the flat units by unitsPerBox DENOM SIGNATURE so a
  // box never mixes two denoms - each partition packs into its OWN box(es) with
  // its OWN capacity, so every box is single-denom and renders an exact "X of N".
  // A single-denom bucket -> one partition -> the pre-Option-B packing is
  // reproduced (byte-identical for orderable orders). Partitions keep
  // first-appearance (FIFO) order, so packing stays deterministic.
  const partitions = new Map<string, FlatUnit[]>();
  for (const u of flat) {
    const sig = denomSig(u);
    const list = partitions.get(sig);
    if (list) list.push(u);
    else partitions.set(sig, [u]);
  }

  const out: CartBox[] = [];
  for (const part of partitions.values()) {
    const partCap = capForUnit(part[0], cap);
    const sizes = BOX_ORDER.filter((c) => partCap[c] !== null);
    if (sizes.length === 0) continue;
    // Non-rose buckets fill QB-default, each box sized to the denom it EXACTLY
    // completes - unless the client forced a group size this partition offers.
    const partTarget =
      groupTarget && partCap[groupTarget] !== null ? groupTarget : undefined;
    out.push(...packQbDefault(part, refVariety, partCap, sizes, partTarget));
  }
  return out;
}

// #468 OPTION-B: the unitsPerBox denom signature (EB/QB/HB) for a unit - the
// packing partition key, so a box holds exactly one denom.
function denomSig(u: FlatUnit): string {
  return BOX_ORDER.map((s) => lineDenom(u.item, s) ?? 0).join("|");
}

// Per-partition capacity (stems), derived from the unit's OWN denom x spb so
// each denom-partition sizes its boxes correctly; falls back to the bucket cap
// for a size the unit does not resolve.
function capForUnit(u: FlatUnit, fallback: BoxCapacity): BoxCapacity {
  const spb = u.item.stemsPerBunch;
  const at = (s: BoxCode): number | null => {
    const d = lineDenom(u.item, s);
    if (d && d > 0 && spb !== null && spb > 0) return d * spb;
    return fallback[s];
  };
  return { EB: at("EB"), QB: at("QB"), HB: at("HB") };
}

// The size a box of `n` bunches EXACTLY completes (smallest such offered size),
// else the QB default. Keeps a COMPLETE box at its true size (freight-correct,
// byte-identical) while an incomplete remainder defaults to the QB nudge.
function sizeForCount(
  units: FlatUnit[],
  sizes: BoxCode[],
  fallback: BoxCode,
): BoxCode {
  const n = units.length;
  const rep = units[0];
  for (const s of sizes) {
    const d = lineDenom(rep.item, s);
    if (d && d === n) return s;
  }
  return fallback;
}

// #468 ROSE LENGTH-MIX: a rose box's per-size bunch capacity is the MAX per-length
// denom across the bunches it holds (all-70 QB = 4; a QB with any 50/60 = 5; HB =
// 12, any lengths). Identical max() rule to computeBoxFill, resolved through the
// same lineDenom source, so the packer and the fill bar/gate always agree.
function roseDenomAt(units: FlatUnit[], size: BoxCode): number {
  let m = 0;
  for (const u of units) {
    const d = lineDenom(u.item, size);
    if (d && d > m) m = d;
  }
  return m;
}

// Drain FIFO into one rose box of `size`, MIXING lengths. A bunch is added while
// the running count stays within the box's CURRENT max-denom capacity - so a
// 50/60 bunch joining an all-70 QB expands the cap 4->5 before the 5th is placed,
// and an all-70 QB closes at 4.
function fillRoseBox(
  flat: FlatUnit[],
  startIdx: number,
  size: BoxCode,
): { units: FlatUnit[]; nextIdx: number } {
  const units: FlatUnit[] = [];
  let i = startIdx;
  while (i < flat.length) {
    const denom = roseDenomAt([...units, flat[i]], size);
    if (denom <= 0) break; // this size not offered
    if (units.length + 1 > denom) break; // would overflow this composition's cap
    units.push(flat[i]);
    i += 1;
  }
  if (units.length === 0 && startIdx < flat.length) {
    units.push(flat[startIdx]);
    i = startIdx + 1;
  }
  return { units, nextIdx: i };
}

// Build a rose box from its drained units: size = QB when the count fits a QB
// (complete at its max-denom, else a partial QB tail), otherwise HB (the HB loop
// produces the fuller boxes). Capacity stems come from the box's own max-denom.
// #468 SIZE DRIVES PACKING: a forced size (the client's group choice) wins, so a
// partial box still reads as the chosen size (e.g. 7 roses -> one partial Large).
function makeRoseBox(
  units: FlatUnit[],
  refVariety: Variety,
  forcedSize?: BoxCode,
): CartBox {
  const spb = units[0]?.item.stemsPerBunch ?? 0;
  const qbD = roseDenomAt(units, "QB");
  const hbD = roseDenomAt(units, "HB");
  const n = units.length;
  const offers = (s: BoxCode) => (s === "QB" ? qbD > 0 : s === "HB" ? hbD > 0 : false);
  let size: BoxCode;
  if (forcedSize && offers(forcedSize)) size = forcedSize;
  else if (qbD > 0 && n <= qbD) size = "QB";
  else if (hbD > 0) size = "HB";
  else size = qbD > 0 ? "QB" : "HB";
  const cap: BoxCapacity = {
    EB: null,
    QB: qbD > 0 && spb > 0 ? qbD * spb : null,
    HB: hbD > 0 && spb > 0 ? hbD * spb : null,
  };
  return makeBunchBox(units, refVariety, size, cap);
}

// A-rose length-mix packer. Default (no forced size): fill full HBs while enough
// remains for a near-full one (>= threshold), then QB boxes for the tail - mirrors
// the pre-mix "grow to largest" shape so a large single-length rose order keeps
// its box COUNT (byte-identical). #468 SIZE DRIVES PACKING: when the client forces
// a group size (Standard=QB / Large=HB), fill EVERY box to that size - so the
// group consolidates (Large) or splits (Standard) exactly as chosen.
function packAroseMix(
  flat: FlatUnit[],
  refVariety: Variety,
  forcedSize?: BoxCode,
): CartBox[] {
  const boxes: CartBox[] = [];
  let idx = 0;
  const forced = forcedSize === "QB" || forcedSize === "HB" ? forcedSize : undefined;
  if (forced) {
    while (idx < flat.length) {
      const { units, nextIdx } = fillRoseBox(flat, idx, forced);
      idx = nextIdx;
      boxes.push(makeRoseBox(units, refVariety, forced));
    }
    return boxes;
  }
  const HB_THRESHOLD = 11; // matches the pre-mix A-rose packer (open HB near-full)
  while (flat.length - idx >= HB_THRESHOLD) {
    const { units, nextIdx } = fillRoseBox(flat, idx, "HB");
    idx = nextIdx;
    boxes.push(makeRoseBox(units, refVariety));
    if (units.length < 12) break; // a partial HB ends the HB loop
  }
  while (idx < flat.length) {
    const { units, nextIdx } = fillRoseBox(flat, idx, "QB");
    idx = nextIdx;
    boxes.push(makeRoseBox(units, refVariety));
  }
  return boxes;
}

// #468 OPTION-B: fillers/specialty fill QB-DEFAULT boxes (Alexis's #61 ladder:
// QB = best value). Each box's SIZE is the one it EXACTLY completes (so a box
// that fills an EB stays an EB - freight-correct + byte-identical), otherwise
// the QB default nudge (an incomplete tail box the client tops off; the header
// ladder shows the EB/HB alternatives). Deterministic (FIFO drain).
// #468 SIZE DRIVES PACKING: `forcedSize` (the client's group choice, already known
// to be offered by this partition) fills every box to that size and labels it that
// size even when the last box is partial - so the group consolidates (larger) or
// splits into more boxes (smaller). Without it, the default QB / exact-completion
// sizing is byte-identical to before.
function packQbDefault(
  flat: FlatUnit[],
  refVariety: Variety,
  cap: BoxCapacity,
  sizes: BoxCode[],
  forcedSize?: BoxCode,
): CartBox[] {
  const fillSize: BoxCode =
    forcedSize && cap[forcedSize] !== null
      ? forcedSize
      : cap.QB !== null
        ? "QB"
        : sizes[sizes.length - 1];
  const boxes: CartBox[] = [];
  let idx = 0;
  while (idx < flat.length) {
    const { units, nextIdx } = fillOneBox(flat, idx, fillSize);
    idx = nextIdx;
    const size =
      forcedSize && cap[forcedSize] !== null
        ? fillSize
        : sizeForCount(units, sizes, fillSize);
    boxes.push(makeBunchBox(units, refVariety, size, cap));
  }
  return boxes;
}

// Group a box's drained units by (slug, length, color) into CartLines.
function makeBunchBox(
  units: FlatUnit[],
  refVariety: Variety,
  size: BoxCode,
  cap: BoxCapacity,
): CartBox {
  type Grp = { item: BucketItem; length: number | undefined; bunches: number };
  const byKey = new Map<string, Grp>();
  for (const u of units) {
    const key = `${u.item.slug}::${u.length ?? "-"}::${u.item.colorChoice ?? ""}`;
    const g = byKey.get(key);
    if (g) g.bunches += 1;
    else byKey.set(key, { item: u.item, length: u.length, bunches: 1 });
  }
  const contents: CartLine[] = [];
  for (const g of byKey.values()) {
    const line = itemToLine(g.item, g.bunches, g.item.stemsPerBunch);
    line.length = g.length;
    contents.push(line);
  }
  return makeMultiLineBox(refVariety.compatibilityGroup, size, cap, contents);
}

// #63 box_stem: build one loose-stem CartLine carrying `stems` stems of a
// box_stem item (quantity === stems; stemsPerBunch null; no boxCode - the box
// size is the group's, set on the box).
function stemLineFrom(item: BucketItem, stems: number): CartLine {
  return {
    slug: item.slug,
    variety: item.variety,
    category: item.category,
    length: item.length,
    unit: "stem",
    quantity: stems,
    stems,
    pricePerStem: item.pricePerStem,
    stemsPerBunch: null,
    colorChoice: item.colorChoice,
    addedAt: item.addedAt,
    boxCode: undefined,
    unitsPerBox: item.unitsPerBox,
    mixGroup: item.mixGroup,
    groupBoxSize: item.groupBoxSize,
  };
}

// #63 box_stem: pack loose-stem items into EB/QB boxes. ONE box holds ONE grade
// (partition by stemShareKey = mixGroup#grade); same-grade colors (separate
// SKUs) share a box, different grades never do. Within a grade, stems drain FIFO
// into boxes sized to the group's chosen size (else QB best-value default),
// splitting a color's stems across boxes when they overflow. Deterministic, so
// the interactive add, the materialize replay, and consolidateBuckets all agree
// (client == server == invoice). HB is skipped (null for hydrangea).
function packStemBucket(items: BucketItem[], refVariety: Variety): CartBox[] {
  const queue = [...items]
    .filter((i) => i.bunches > 0)
    .sort((a, b) => {
      const t = a.addedAt.localeCompare(b.addedAt);
      return t !== 0 ? t : a.slug.localeCompare(b.slug);
    });
  if (queue.length === 0) return [];

  // Partition by grade (stemShareKey resolves grade+mixGroup LIVE by slug). A
  // slug that does not resolve to a box_stem variety falls back to its denom
  // signature so it still packs into its own boxes rather than merging grades.
  const partitions = new Map<string, BucketItem[]>();
  for (const it of queue) {
    const key = stemShareKey(it.slug) ?? denomSig({ item: it, length: it.length });
    const list = partitions.get(key);
    if (list) list.push(it);
    else partitions.set(key, [it]);
  }

  const out: CartBox[] = [];
  for (const part of partitions.values()) {
    const rep = part[0];
    const groupTarget = part.find((i) => i.groupBoxSize)?.groupBoxSize;
    const denomAt = (s: BoxCode): number => lineDenom(rep, s) ?? 0; // stems/box
    const offered = BOX_ORDER.filter((s) => denomAt(s) > 0); // EB, QB (HB null)
    if (offered.length === 0) continue;
    const fillSize: BoxCode =
      groupTarget && denomAt(groupTarget) > 0
        ? groupTarget
        : denomAt("QB") > 0
          ? "QB"
          : offered[offered.length - 1];
    const capStemsFill = denomAt(fillSize);

    // Drain the partition's stems FIFO, splitting a line across boxes as needed.
    const pending = part.map((it) => ({ item: it, remaining: it.bunches }));
    let pi = 0;
    while (pi < pending.length) {
      const byKey = new Map<string, { item: BucketItem; stems: number }>();
      let boxStems = 0;
      while (pi < pending.length && boxStems < capStemsFill) {
        const p = pending[pi];
        if (p.remaining <= 0) {
          pi += 1;
          continue;
        }
        const take = Math.min(p.remaining, capStemsFill - boxStems);
        const k = `${p.item.slug}::${p.item.colorChoice ?? ""}`;
        const g = byKey.get(k);
        if (g) g.stems += take;
        else byKey.set(k, { item: p.item, stems: take });
        boxStems += take;
        p.remaining -= take;
        if (p.remaining <= 0) pi += 1;
      }
      // Size: a forced group size wins; else the size this box EXACTLY fills
      // (readyNow), else the QB/fill default (an incomplete tail box).
      const size: BoxCode =
        groupTarget && denomAt(groupTarget) > 0
          ? fillSize
          : (offered.find((s) => denomAt(s) === boxStems) ?? fillSize);
      const contents = [...byKey.values()].map((g) =>
        stemLineFrom(g.item, g.stems),
      );
      out.push({
        id: newId(),
        compatibilityGroup: refVariety.compatibilityGroup,
        size,
        capacity: { stems: denomAt(size) },
        contents,
      });
    }
  }
  return out;
}

// DIRECTIVE #022 V3 packer: greedy-fill HBs (above threshold) then QBs,
// then 1 partial QB for leftover. Items drained FIFO across varieties
// produces mixed-variety boxes when totals span variety boundaries.
function packBucket(
  items: BucketItem[],
  refVariety: Variety,
  cap: BoxCapacity,
): CartBox[] {
  const queue = [...items]
    .filter((i) => i.bunches > 0)
    .sort((a, b) => {
      const t = a.addedAt.localeCompare(b.addedAt);
      return t !== 0 ? t : a.slug.localeCompare(b.slug);
    });

  if (queue.length === 0) return [];

  // #63 box_stem: loose-stem items pack via the dedicated stem drain (grade
  // partition + EB/QB stem fill). Any non-stem items in the same bucket (none
  // in practice - hydrangea whole boxes are box-mode and never dissolved) keep
  // the existing path.
  const stemItems = queue.filter((i) => i.unit === "stem");
  if (stemItems.length > 0) {
    const rest = queue.filter((i) => i.unit !== "stem");
    const stemBoxes = packStemBucket(stemItems, refVariety);
    const restBoxes =
      rest.length > 0 ? packBucket(rest, refVariety, cap) : [];
    return [...stemBoxes, ...restBoxes];
  }

  const smallest = smallestAvailableBox(cap);
  if (!smallest) return [];
  const smallestCap = stemsInBox(cap, smallest);
  if (smallestCap === null || smallestCap <= 0) return [];

  // Hydrangea + other box-unit varieties: stemsPerBunch is null/0, so
  // the bunch-based packer doesn't apply. Emit one CartBox per "bunch
  // unit" in the BucketItem, where each unit = 1 box at the buyer-
  // picked size (item.boxCode). Falls back to smallest only when no
  // boxCode was provided (legacy callers).
  //
  // DIRECTIVE #024 fix: previously this branch always used
  // smallestAvailableBox(cap), discarding the resolved boxCode that
  // buildLine carried through. That meant Power Buyer (silent default)
  // and detail-page explicit QB picks BOTH produced EB cart boxes for
  // hydrangea. Now item.boxCode wins.
  const spb = refVariety.stemsPerBunch ?? queue[0].stemsPerBunch;
  if (!spb || spb <= 0) {
    const boxes: CartBox[] = [];
    for (const it of queue) {
      const fitSize: BoxCode =
        it.boxCode && cap[it.boxCode] !== null ? it.boxCode : smallest;
      const stemsAtFit = stemsInBox(cap, fitSize) ?? 0;
      for (let i = 0; i < it.bunches; i++) {
        const oneLine = itemToLine(it, 1, 0);
        oneLine.stems = stemsAtFit;
        oneLine.quantity = 1;
        oneLine.boxCode = fitSize;
        boxes.push(
          makeMultiLineBox(
            refVariety.compatibilityGroup,
            fitSize,
            cap,
            [oneLine],
          ),
        );
      }
    }
    return boxes;
  }

  // #468-ENFORCEMENT A1: fractional box-share packing. Replaces packAroseV4's
  // length cap AND the growBoxPlan spb path with one drain that reads each
  // bunch's own unitsPerBox denominator - so roses mix lengths, groups mix
  // varieties with different bunch counts, and edited counts are honored, all
  // completing a box exactly at share 1.0.
  return packBunchBucket(queue, refVariety, cap);
}

// #430 CONSOLIDATION post-pass. materializeCart replays items one-at-a-time
// (forceAccept), which sizes the FIRST box for the FIRST item and then spills a
// small trailing remainder into a lone near-empty box (the #4686 group-E
// Scabiosa case: 120 filler stems -> EB(100) + a 20/80 EB). This re-packs each
// multi-box bucket via the WHOLE-bucket packBucket planner - the same one the
// interactive add path uses - and keeps the re-pack ONLY when it strictly
// reduces the box count. Guarantees:
//   - segregation (#353) preserved: each bucket is a single sub-category; never
//     merges across buckets/categories;
//   - capacities unchanged: only the box-size CHOICE improves;
//   - no churn: a bucket that already packs optimally is byte-identical;
//   - determinism: buckets processed in first-seen order, ref chosen by max
//     capacity with a slug tiebreak;
//   - parity (#384): runs inside materializeCart, so cart, server packing, and
//     the invoice all consolidate identically.
export function consolidateBuckets(
  cart: Cart,
  lookup: Map<string, Variety>,
): Cart {
  const boxModeBoxes = cart.boxes.filter(isBoxModeBox);
  const packable = cart.boxes.filter((b) => !isBoxModeBox(b));

  // #468-ENFORCEMENT A1: bucket key = MIX GROUP only (all lengths/grades merge),
  // matching dissolveBucket.
  const keyOf = (b: CartBox): string | null => {
    const g = boxMixGroup(b);
    return g === null ? null : `g${g}`;
  };

  const groups = new Map<string, CartBox[]>();
  const order: string[] = [];
  for (const b of packable) {
    const key = keyOf(b) ?? `__solo${order.length}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(b);
  }

  const resultBoxes: CartBox[] = [...boxModeBoxes];
  for (const key of order) {
    const boxes = groups.get(key)!;
    if (boxes.length < 2) {
      resultBoxes.push(...boxes);
      continue;
    }
    const bucketItems: BucketItem[] = [];
    for (const b of boxes) {
      for (const line of b.contents) mergeBucketItem(bucketItems, lineToItem(line));
    }
    const ref = pickConsolidationRef(bucketItems, lookup);
    const cap = ref
      ? boxCapacityForLength(ref, bucketItems[0]?.length ?? 0)
      : null;
    if (!ref || !cap) {
      resultBoxes.push(...boxes);
      continue;
    }
    const repacked = packBucket(bucketItems, ref, cap);
    // Accept the re-pack ONLY when it (a) strictly reduces the box count,
    // (b) preserves every stem, and (c) never overfills a box. A bucket with
    // mixed stems-per-bunch can make packBucket's single-spb plan overfill a
    // box (e.g. 155 stems into a 140-cap QB); reject those and keep the safe
    // incremental packing.
    const usedStems = (bs: CartBox[]) =>
      bs.reduce((s, b) => s + b.contents.reduce((x, c) => x + c.stems, 0), 0);
    // #468-ENFORCEMENT A1: overfill is now fractional (no box's shares exceed
    // 1.0), not a raw stem cap - an edited unitsPerBox can hold more stems than
    // the matrix boxSizes.
    const noOverfill = repacked.every(
      (b) => computeBoxFill(b).totalShare <= 1 + EPS_PACK,
    );
    if (
      repacked.length > 0 &&
      repacked.length < boxes.length &&
      noOverfill &&
      usedStems(repacked) === usedStems(boxes)
    ) {
      resultBoxes.push(...repacked);
    } else {
      resultBoxes.push(...boxes);
    }
  }

  return recompute({ ...cart, boxes: resultBoxes });
}

// The bucket variety whose single-box capacity is largest, so packBucket can
// consolidate into the fewest boxes. Deterministic (alphabetical slug tiebreak).
function pickConsolidationRef(
  items: BucketItem[],
  lookup: Map<string, Variety>,
): Variety | null {
  let best: Variety | null = null;
  let bestCap = -1;
  for (const it of [...items].sort((a, b) => a.slug.localeCompare(b.slug))) {
    const v = lookup.get(it.slug);
    if (!v) continue;
    const sizes = boxCapacityForLength(v, it.length ?? 0);
    if (!sizes) continue;
    const maxCap = Math.max(
      ...(["EB", "QB", "HB"] as BoxCode[]).map((c) => sizes[c] ?? 0),
    );
    if (maxCap > bestCap) {
      bestCap = maxCap;
      best = v;
    }
  }
  return best;
}


function itemToLine(item: BucketItem, bunches: number, spb: number): CartLine {
  // PASTE #384: keep each line's OWN stems-per-bunch. The old `spb > 0 ? spb`
  // rewrote every line in a mixed box to the box's REFERENCE spb - the "25 on
  // everything" (a carnation at spb 25 corrupted the spb-10 varieties sharing
  // its box, inflating their stems + mispricing). Prefer the item's own spb;
  // the passed ref spb is only a fallback when the item genuinely has none.
  const usedSpb = item.stemsPerBunch > 0 ? item.stemsPerBunch : spb;
  const stems = bunches * usedSpb;
  return {
    slug: item.slug,
    variety: item.variety,
    category: item.category,
    length: item.length,
    unit: item.unit,
    quantity: item.unit === "bunch" ? bunches : 1,
    stems,
    pricePerStem: item.pricePerStem,
    stemsPerBunch: usedSpb,
    colorChoice: item.colorChoice,
    addedAt: item.addedAt,
    boxCode: item.boxCode,
    unitsPerBox: item.unitsPerBox,
    mixGroup: item.mixGroup,
    groupBoxSize: item.groupBoxSize,
  };
}

function makeMultiLineBox(
  compatibilityGroup: CompatibilityGroup,
  size: BoxCode,
  cap: BoxCapacity,
  contents: CartLine[],
): CartBox {
  return {
    id: newId(),
    compatibilityGroup,
    size,
    capacity: rawCapacityFor(cap, size) ?? { stems: 0 },
    contents,
  };
}

// LEGACY: still used by forceAccept fallback + repackSameLine. Kept for
// hydrangea single-add + mix-rule cap fallback paths; the V3 bucket
// packer replaces it for normal addToBox flow.
function packBunchesIntoBoxes(
  line: CartLine,
  variety: Variety,
  cap: BoxCapacity,
  totalStems: number,
): CartBox[] {
  const smallest = smallestAvailableBox(cap);
  if (!smallest) return [];
  const smallestCap = stemsInBox(cap, smallest);
  if (smallestCap === null || smallestCap <= 0) return [];

  // Hydrangea + other box-unit single-add path: no split, just pick the
  // smallest fitting size.
  if (line.unit === "box") {
    const fitSize = chooseInitialBoxSize(cap, totalStems) ?? smallest;
    return [newBoxOfSize(chunkLine(line, totalStems, line.stemsPerBunch ?? 0), variety, fitSize, cap)];
  }

  const spb = line.stemsPerBunch ?? 0;
  if (spb <= 0) {
    // Defensive: bunch unit but no stemsPerBunch. Treat as single box.
    const fitSize = chooseInitialBoxSize(cap, totalStems) ?? smallest;
    return [newBoxOfSize(chunkLine(line, totalStems, 0), variety, fitSize, cap)];
  }

  const totalBunches = Math.round(totalStems / spb);

  // #468-ENFORCEMENT A1: route through the same fractional packer as the bucket
  // path, so a single-variety add sizes its boxes from unitsPerBox too.
  const item: BucketItem = { ...lineToItem(line), bunches: totalBunches };
  return packBunchBucket([item], variety, cap);
}

// Helper: derive a new CartLine that carries `stems` stems from the
// original line, with proportionally-scaled quantity for bunch units.
function chunkLine(
  source: CartLine,
  stems: number,
  stemsPerBunch: number,
): CartLine {
  if (source.unit === "bunch" && stemsPerBunch > 0) {
    return {
      ...source,
      stems,
      quantity: Math.round(stems / stemsPerBunch),
    };
  }
  // For "box" units we keep quantity=1 per box; the source qty>1 case
  // is handled by the caller looping per box at the add site.
  return { ...source, stems, quantity: 1 };
}

function newBoxOfSize(
  line: CartLine,
  variety: Variety,
  size: BoxCode,
  cap: BoxCapacity,
): CartBox {
  return {
    id: newId(),
    compatibilityGroup: variety.compatibilityGroup,
    size,
    capacity: rawCapacityFor(cap, size) ?? { stems: 0 },
    contents: [line],
  };
}

function replaceBox(cart: Cart, replacement: CartBox): Cart {
  return {
    ...cart,
    boxes: cart.boxes.map((b) => (b.id === replacement.id ? replacement : b)),
  };
}

// Convenience: returns the totals for a single box (stems, bunches, fill%).
export function summarizeBox(box: CartBox): {
  stems: number;
  bunches: number;
  fillPct: number;
} {
  const stems = box.contents.reduce((s, c) => s + c.stems, 0);
  const bunches = box.contents.reduce(
    (b, c) => b + (c.unit === "bunch" ? c.quantity : 0),
    0,
  );
  const fillPct = box.capacity.stems > 0
    ? Math.min(100, Math.round((stems / box.capacity.stems) * 100))
    : 0;
  return { stems, bunches, fillPct };
}

export { EMPTY_CART };
